import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../features/auth/useSession';
import { useCurrentOrg } from '../features/orgs/useCurrentOrg';
import { Sidebar } from '../components/Sidebar';
import { TopBar } from '../components/TopBar';

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/wallets': 'Wallets',
  '/api-keys': 'API keys',
  '/authentication': 'Authentication',
  '/recovery': 'Recovery',
  '/audit': 'Audit log',
  '/settings': 'Settings',
};

export function AppLayout() {
  const session = useSession();
  const orgs = useCurrentOrg();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (session.data === null) {
      navigate('/login', { replace: true });
    }
  }, [session.data, navigate]);

  useEffect(() => {
    if (session.data && !orgs.isLoading && !orgs.org && location.pathname !== '/onboarding') {
      navigate('/onboarding', { replace: true });
    }
  }, [session.data, orgs.isLoading, orgs.org, location.pathname, navigate]);

  if (session.isLoading || orgs.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-ink-tertiary">
        Loading…
      </div>
    );
  }

  if (session.isError) {
    return (
      <div className="mx-auto mt-24 max-w-md rounded-xl border border-bad/30 bg-bad/[0.04] p-6 text-sm text-bad">
        <p className="font-medium">Couldn't reach the API.</p>
        <p className="mt-1">
          {session.error instanceof Error ? session.error.message : 'Unknown error'}
        </p>
        <p className="mt-3">
          Make sure the backend is running on <code>localhost:8081</code> and the docker stack
          (Postgres, Kratos) is up.
        </p>
      </div>
    );
  }

  if (!session.data) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-ink-tertiary">
        Redirecting…
      </div>
    );
  }

  if (!orgs.org) {
    return (
      <div className="min-h-screen bg-surface-soft text-ink-primary">
        <Outlet />
      </div>
    );
  }

  const title = PAGE_TITLES[location.pathname] ?? 'Dashboard';

  return (
    <div className="flex min-h-screen bg-surface-soft text-ink-primary">
      <Sidebar orgName={orgs.org.name} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar title={title} email={session.data.email} />
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
